/**
 * This component composes the Address fields section of the Vendor Application
 */
import { capitalize, chain, get, keyBy, size, values, isEqual } from 'lodash';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import Input from '@mui/material/Input';

import CountryList from '../../Lib/Constants/countries';
import StatesList from '../../Lib/Constants/states';
import styles from '../VendorApplication/VendorApplication.module.css';
import { getAddressKey } from '../../Lib/Utils/FormUtils/address-fields';
import TextInput from '../ReusableComponents/TextInput/TextInput';
import SelectInput from '../ReusableComponents/SelectInput/SelectInput';
import isDomestic from '../../Lib/Utils/VendorApplication/is-domestic';
/**
 * Creates the individual input elements of the dropdown for country selection
 */
const makeCountrySelectInput = (country, prefix) => (
  <Input name="country" id={`${prefix}-country`} value={country || ''} />
);

const makeStateSelectInput = (state, prefix) => (
  <Input name="state" id={`${prefix}-state`} value={state || ''} />
);

const mapState = (state) => {
  if (size(state) === 2) return state;
  const statesByText = keyBy(StatesList, 'text');
  const formatted = chain(state)
    .split(' ')
    .map((s) => capitalize(s))
    .join(' ')
    .value();
  return get(statesByText, `${formatted}.value`);
};

class AddressFields extends Component {
  constructor(props) {
    super(props);

    const {
      values: { country, address, address_2, city, state, zip },
    } = props;

    /**
     * the local state on render uses any fields that are on the user record
     * already, to be updated if not originally submitted
     */
    this.state = {
      country,
      address,
      address_2,
      city,
      state,
      zip,
    };

    this.handleChange = this.handleChange.bind(this);
    this.onBlur = this.onBlur.bind(this);
  }

  /**
   * Maybe update component when props change
   * @param prevProps
   */
  componentDidUpdate(prevProps) {
    if (!isEqual(values(prevProps.values), values(this.props.values))) {
      this.setState({
        ...this.props.values,
      });
    }
  }

  /**
   * When moving focus off of an input, applies validation to fields in real
   * time via onBlur function in props
   *
   * @param {object} target: {
   *  @property {string} name: the input name
   *  @property {string} value: the input value
   *   }
   */
  onBlur({ target: { name, value } }) {
    this.props.onBlur({ target: { name, value } });
  }

  /**
   * Updates local state with the input values to control the inputs
   *
   * @param {object} target: {
   *   @property {string} name: the input name
   *   @property {string} value: the input value
   *  }
   */
  handleChange({ target: { name, value } }) {
    const key = getAddressKey(name);
    this.setState({ [key]: value }, () => this.props.onChange(name, value));
  }

  render() {
    const { country, address, address_2, city, state, zip } = this.state;
    const {
      disabled,
      domestic,
      names: {
        country_field,
        address_field,
        address_2_field,
        city_field,
        state_field,
        zip_field,
      },
      prefix,
      emptyFields,
      required,
      validationErrors,
      isVendorApplication,
    } = this.props;

    return (
      <div>
        <div className="flex flex-col md:flex-row gap-4 pb-4">
          <SelectInput
            required
            fullWidth
            className={styles.rowItem}
            error={required && !!emptyFields[country_field]}
            native
            id={`${prefix}-country`}
            disabled={disabled}
            value={country || ''}
            onChange={this.handleChange}
            onBlur={this.onBlur}
            label="Country"
            autoComplete="no"
            input={makeCountrySelectInput(country, prefix)}
            name={country_field}
            inputProps={{
              name: `${country_field}`,
              id: `${prefix}-vendor-country`,
            }}
            options={CountryList}
          />

          <TextInput
            className={styles.rowItem}
            required={required}
            fullWidth
            error={
              required &&
              (!!emptyFields[address_field] ||
                !!validationErrors?.[address_field])
            }
            variant="outlined"
            onChange={this.handleChange}
            onBlur={this.onBlur}
            name={address_field}
            value={address || ''}
            id={`${prefix}-address`}
            autoComplete="no"
            disabled={disabled}
            label="Street Address"
            formHelperText={validationErrors?.[address_field]}
          />

          <TextInput
            className={styles.rowItem}
            fullWidth
            label="Apt, Suite, etc."
            variant="outlined"
            onChange={this.handleChange}
            onBlur={this.onBlur}
            name={address_2_field}
            value={address_2 || ''}
            id={`${prefix}-address-2`}
            autoComplete="no"
            disabled={disabled}
            error={!!validationErrors?.[address_2_field]}
            formHelperText={validationErrors?.[address_2_field]}
          />
        </div>

        <div className="flex flex-col md:flex-row gap-4">
          <TextInput
            className={styles.rowItem}
            fullWidth
            required={required}
            error={
              required &&
              (!!emptyFields[city_field] || !!validationErrors?.[city_field])
            }
            label="City"
            variant="outlined"
            name={city_field}
            value={city || ''}
            id={`${prefix}-city`}
            onChange={this.handleChange}
            onBlur={this.onBlur}
            autoComplete="no"
            disabled={disabled}
            formHelperText={validationErrors?.[city_field]}
          />

          {domestic ? (
            <SelectInput
              fullWidth
              required={required}
              error={
                required &&
                (emptyFields[state_field] || !!validationErrors?.[state_field])
              }
              className={styles.rowItem}
              native
              id={`${prefix}-state`}
              disabled={disabled}
              value={mapState(state) || ''}
              onChange={this.handleChange}
              onBlur={this.onBlur}
              autoComplete="no"
              input={makeStateSelectInput(state, prefix)}
              name={state_field}
              inputProps={{
                name: `${state_field}`,
                id: `${prefix}-state`,
              }}
              options={StatesList}
              label="State"
              formHelperText={validationErrors?.[state_field]}
            />
          ) : (
            <TextInput
              fullWidth
              className={styles.rowItem}
              variant="outlined"
              id={`${prefix}-state`}
              disabled={disabled}
              value={state || ''}
              onChange={this.handleChange}
              onBlur={this.onBlur}
              name={state_field}
              autoComplete="no"
              label="State or Province"
              error={!!validationErrors?.[state_field]}
              formHelperText={validationErrors?.[state_field]}
            />
          )}

          <TextInput
            label={domestic ? 'Zip Code' : 'Postal Code'}
            className={styles.rowItem}
            fullWidth
            required={isVendorApplication ? isDomestic(country) : required}
            error={
              required &&
              (!!emptyFields[zip_field] || !!validationErrors?.[zip_field])
            }
            variant="outlined"
            onChange={this.handleChange}
            onBlur={this.onBlur}
            name={zip_field}
            value={zip || ''}
            id={`${prefix}-zip`}
            autoComplete="no"
            disabled={disabled}
            formHelperText={validationErrors?.[zip_field]}
          />
        </div>
      </div>
    );
  }
}

AddressFields.propTypes = {
  values: PropTypes.shape({
    country: PropTypes.string,
    address: PropTypes.string,
    address_2: PropTypes.string,
    city: PropTypes.string,
    state: PropTypes.string,
    zip: PropTypes.string,
  }).isRequired,
  names: PropTypes.shape({
    country_field: PropTypes.string,
    address_field: PropTypes.string,
    address_2_field: PropTypes.string,
    city_field: PropTypes.string,
    state_field: PropTypes.string,
    zip_field: PropTypes.string,
  }).isRequired,
  prefix: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onBlur: PropTypes.func.isRequired,
  emptyFields: PropTypes.shape({}).isRequired,
  domestic: PropTypes.bool.isRequired,
  disabled: PropTypes.bool.isRequired,
  required: PropTypes.bool,
  validationErrors: PropTypes.shape({}),
  isVendorApplication: PropTypes.bool,
};

AddressFields.defaultProps = {
  required: false,
};

export default AddressFields;
